'use client';

import { Dialog } from '@base-ui/react/dialog';
import { css } from '@repo/pandacss/css';
import { CloseIcon } from '@repo/ui/icons/CloseIcon';
import { getOrCreateId } from '@repo/utils/static-ids';
import { useRef } from 'react';
import { ExitModal } from '@/features/funnel/components/ExitModal';
import { useExitModalCloseAction } from '@/features/funnel/components/ExitModal/useExitModalCloseAction';
import { useIsExitIntentEnabled } from '@/features/funnel/exit-intent/Context';
import { useBranchedPageAction } from '@/features/shared/analytics/hooks/useBranchedPageAction';

export function CloseFlow({
  desktopOnly = false,
  isDarkVariant = false,
}: {
  desktopOnly?: boolean;
  isDarkVariant?: boolean;
}) {
  const isExitIntentEnabled = useIsExitIntentEnabled();
  const openActivityIdRef = useRef('');
  const openActivityId = getOrCreateId(openActivityIdRef);
  const fireClosePageActionAnalyticsEvent = useBranchedPageAction({
    actionName: 'cardfinder_flow_exit',
    actionType: 'select',
    actionInput: 'exit',
    activityId: openActivityId,
  });

  const { featureViewedActivityId, fireCloseAction } = useExitModalCloseAction();

  if (isExitIntentEnabled) return null;

  return (
    <Dialog.Root
      // trap-focus: keep focus in the dialog without locking document scroll (avoids scrollbar layout shift).
      modal="trap-focus"
      onOpenChange={open => {
        if (!open) {
          fireCloseAction();
        }
      }}
    >
      <Dialog.Trigger
        render={
          <button
            type="button"
            className={css({
              width: '[20px]',
              height: '[20px]',
              lg: {
                position: 'absolute',
                top: '[25px]',
                right: '[25px]',
                width: '[24px]',
                height: '[24px]',
              },
              cursor: 'pointer',
              border: 'none',
              rounded: 'full',
              color: {
                base: 'neutral.darker',
                lg: isDarkVariant ? 'neutral.darker' : 'common.white',
              },
              px: '[0]',
              bg: '[transparent]',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              _hover: {
                bg: {
                  base: 'neutral.lightest',
                  lg: isDarkVariant ? 'neutral.lightest' : 'greens.dark',
                },
              },
              ...(desktopOnly && {
                display: { base: 'none', lg: 'flex' },
              }),
            })}
            aria-label="Close credit card finder"
            onClick={() => fireClosePageActionAnalyticsEvent()}
          >
            <CloseIcon fill="currentColor" />
          </button>
        }
      />
      <ExitModal featureViewedActivityId={featureViewedActivityId} />
    </Dialog.Root>
  );
}
